import {
  Alert,
  Avatar,
  Badge,
  Button,
  EmptyState,
  PageHeader,
  SelectField,
  Spinner,
  TextAreaField,
  TextField,
  useConfirm,
  useToast,
} from '@cartly/ui';
import { Bot, Camera, Inbox, MessageCircle, StickyNote, Zap } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import { api, errorMessage } from '../../api/client';
import { formatDateTime, formatRelative } from '../../app/format';
import { useLoader, usePaged } from '../../app/hooks';
import { useActiveSession, useSession } from '../../app/session';
import { QuickRepliesManager } from './QuickRepliesManager';
import {
  contactLabel,
  type Conversation,
  type ConvStatus,
  type Detail,
  type Note,
  type QuickReply,
  type ThreadMessage,
} from './types';
import { useInboxEvents, type InboxEvent } from './useInboxEvents';

interface Counts {
  open: number;
  mine: number;
  unassigned: number;
  escalated: number;
  unread: number;
}
interface Assignee {
  id: string;
  name: string;
  role: string;
}

const STATUS_LABEL: Record<ConvStatus, string> = {
  open: 'Open',
  pending: 'Waiting on customer',
  resolved: 'Resolved',
};

const EVENT_LABEL: Record<string, string> = {
  assigned: 'Assigned',
  unassigned: 'Unassigned',
  status_changed: 'Status changed',
  escalated: 'Handed to a person',
  ai_resumed: 'Assistant resumed',
  handoff: 'Handoff rule fired',
};

function ChannelIcon({ channel }: { channel: Conversation['channel'] }) {
  return channel === 'whatsapp' ? (
    <MessageCircle size={14} aria-label="WhatsApp" />
  ) : (
    <Camera size={14} aria-label="Instagram" />
  );
}

/** One place for WhatsApp and Instagram conversations: triage, reply, notes, assignment. */
export function InboxPage() {
  const session = useActiveSession();
  const { can } = useSession();
  const tenantId = session.activeTenant?.id;
  const [params, setParams] = useSearchParams();
  const selectedId = params.get('c');
  const [status, setStatus] = useState<'open' | 'pending' | 'resolved'>('open');
  const [assignee, setAssignee] = useState<'any' | 'me' | 'unassigned'>('any');
  const [channel, setChannel] = useState<'' | 'whatsapp' | 'instagram'>('');
  const [escalated, setEscalated] = useState(false);
  const [q, setQ] = useState('');
  const [debouncedQ, setDebouncedQ] = useState('');
  const [manageQuick, setManageQuick] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(q.trim()), 300);
    return () => clearTimeout(t);
  }, [q]);

  const filterKey = `${tenantId}|${status}|${assignee}|${channel}|${escalated}|${debouncedQ}`;
  const list = usePaged<Conversation>(async (cursor) => {
    const qs = new URLSearchParams({ status });
    if (assignee !== 'any') qs.set('assignee', assignee);
    if (channel) qs.set('channel', channel);
    if (escalated) qs.set('escalated', '1');
    if (debouncedQ) qs.set('q', debouncedQ);
    if (cursor) qs.set('cursor', cursor);
    const r = await api.get<{ conversations: Conversation[]; nextCursor: string | null }>(
      `/inbox/conversations?${qs}`,
    );
    return { items: r.conversations, nextCursor: r.nextCursor };
  }, filterKey);
  const counts = useLoader(() => api.get<Counts>('/inbox/counts'), tenantId);
  const quick = useLoader(
    () =>
      api.get<{ quickReplies: QuickReply[] }>('/inbox/quick-replies').then((r) => r.quickReplies),
    tenantId,
  );
  const assignees = useLoader(
    () => api.get<{ assignees: Assignee[] }>('/inbox/assignees').then((r) => r.assignees),
    tenantId,
  );

  // Refresh list, counts and the open thread on real-time events (debounced to one pass).
  const [tick, setTick] = useState(0);
  const refreshTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const { reload: reloadList } = list;
  const { reload: reloadCounts } = counts;
  const onEvent = useCallback(
    (_event: InboxEvent) => {
      if (refreshTimer.current) clearTimeout(refreshTimer.current);
      refreshTimer.current = setTimeout(() => {
        void reloadList();
        void reloadCounts();
        setTick((t) => t + 1);
      }, 250);
    },
    [reloadList, reloadCounts],
  );
  const live = useInboxEvents(onEvent, tenantId);
  useEffect(() => {
    // Without a live socket the inbox still refreshes, just less often.
    if (live) return;
    const t = setInterval(
      () => onEvent({ type: 'conversation.updated', conversationId: '' }),
      20_000,
    );
    return () => clearInterval(t);
  }, [live, onEvent]);

  const select = (id: string | null) => {
    const next = new URLSearchParams(params);
    if (id) next.set('c', id);
    else next.delete('c');
    setParams(next, { replace: true });
  };

  const refreshAll = async () => {
    await Promise.all([list.reload(), counts.reload()]);
    setTick((t) => t + 1);
  };

  return (
    <div className="stack-lg">
      <PageHeader
        title="Inbox"
        description="WhatsApp and Instagram conversations in one place."
        actions={
          can('inbox:assign') ? (
            <Button size="sm" onClick={() => setManageQuick(true)}>
              Quick replies
            </Button>
          ) : undefined
        }
      />
      {!live && (
        <Alert tone="warning">
          Live updates are not connected; the inbox refreshes every 20 seconds.
        </Alert>
      )}
      <div className="inbox">
        <aside className="inbox__list" aria-label="Conversations">
          <div className="inbox__filters">
            <TextField
              label="Search"
              placeholder="Name, number or message text"
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />
            <div className="inbox__filter-row">
              <SelectField
                label="Status"
                value={status}
                onChange={(e) => setStatus(e.target.value as typeof status)}
                options={[
                  { value: 'open', label: `Open${counts.data ? ` (${counts.data.open})` : ''}` },
                  { value: 'pending', label: 'Waiting on customer' },
                  { value: 'resolved', label: 'Resolved' },
                ]}
              />
              <SelectField
                label="Assigned to"
                value={assignee}
                onChange={(e) => setAssignee(e.target.value as typeof assignee)}
                options={[
                  { value: 'any', label: 'Everyone' },
                  { value: 'me', label: `Me${counts.data ? ` (${counts.data.mine})` : ''}` },
                  {
                    value: 'unassigned',
                    label: `Unassigned${counts.data ? ` (${counts.data.unassigned})` : ''}`,
                  },
                ]}
              />
            </div>
            <div className="inbox__filter-row">
              <SelectField
                label="Channel"
                value={channel}
                onChange={(e) => setChannel(e.target.value as typeof channel)}
                options={[
                  { value: '', label: 'All channels' },
                  { value: 'whatsapp', label: 'WhatsApp' },
                  { value: 'instagram', label: 'Instagram' },
                ]}
              />
              <label className="inbox__check">
                <input
                  type="checkbox"
                  checked={escalated}
                  onChange={(e) => setEscalated(e.target.checked)}
                />
                Needs a person{counts.data ? ` (${counts.data.escalated})` : ''}
              </label>
            </div>
          </div>
          {list.loading ? (
            <div className="page-loading">
              <Spinner size="lg" label="Loading" />
            </div>
          ) : list.error ? (
            <Alert tone="critical">{list.error}</Alert>
          ) : list.items.length === 0 ? (
            <EmptyState icon={<Inbox size={22} />} title="No conversations here">
              {debouncedQ
                ? 'Nothing matches this search.'
                : 'Customer messages from WhatsApp and Instagram appear here as they arrive.'}
            </EmptyState>
          ) : (
            <ul className="inbox__items">
              {list.items.map((c) => (
                <li key={c.id}>
                  <button
                    type="button"
                    className={
                      'inbox-item' +
                      (c.id === selectedId ? ' inbox-item--active' : '') +
                      (c.unread > 0 ? ' inbox-item--unread' : '')
                    }
                    onClick={() => select(c.id)}
                  >
                    <Avatar name={contactLabel(c)} />
                    <span className="inbox-item__body">
                      <span className="inbox-item__top">
                        <span className="inbox-item__name">{contactLabel(c)}</span>
                        <span className="inbox-item__time">{formatRelative(c.lastMessageAt)}</span>
                      </span>
                      <span className="inbox-item__preview">
                        <ChannelIcon channel={c.channel} />
                        <span>
                          {c.lastDirection === 'outbound' ? 'You: ' : ''}
                          {c.preview ?? '—'}
                        </span>
                      </span>
                      <span className="inbox-item__meta">
                        {c.aiState === 'escalated' && <Badge tone="warning">Needs a person</Badge>}
                        {c.assignee && <Badge>{c.assignee.name ?? 'Assigned'}</Badge>}
                        {c.unread > 0 && <Badge tone="primary">{c.unread} new</Badge>}
                      </span>
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
          {list.hasMore && (
            <div className="card__footer">
              <Button size="sm" loading={list.loadingMore} onClick={() => void list.loadMore()}>
                Load more
              </Button>
            </div>
          )}
        </aside>

        {selectedId ? (
          <ConversationView
            key={selectedId}
            id={selectedId}
            tick={tick}
            quick={quick.data ?? []}
            assignees={assignees.data ?? []}
            onChanged={refreshAll}
          />
        ) : (
          <section className="inbox__empty">
            <EmptyState icon={<Inbox size={22} />} title="Select a conversation">
              Pick a conversation on the left to read and reply.
            </EmptyState>
          </section>
        )}
      </div>
      {manageQuick && (
        <QuickRepliesManager
          items={quick.data ?? []}
          onClose={() => setManageQuick(false)}
          onChanged={() => void quick.reload()}
        />
      )}
    </div>
  );
}

function ConversationView({
  id,
  tick,
  quick,
  assignees,
  onChanged,
}: {
  id: string;
  tick: number;
  quick: QuickReply[];
  assignees: Assignee[];
  onChanged: () => Promise<void>;
}) {
  const toast = useToast();
  const confirm = useConfirm();
  const { can } = useSession();
  const session = useActiveSession();
  const myId = session.user.id;
  const detail = useLoader(() => api.get<Detail>(`/inbox/conversations/${id}`), id);
  const thread = useLoader(
    () =>
      api.get<{ messages: ThreadMessage[]; hasMore: boolean }>(
        `/inbox/conversations/${id}/messages?limit=100`,
      ),
    id,
  );
  const notes = useLoader(
    () => api.get<{ notes: Note[] }>(`/inbox/conversations/${id}/notes`).then((r) => r.notes),
    id,
  );
  const draft = useLoader(
    () =>
      api
        .get<{ draft: { text: string; createdAt: string } | null }>(`/ai/conversations/${id}/draft`)
        .then((r) => r.draft),
    id,
  );
  // Real-time events bump `tick`: refetch in place so the thread never flashes empty.
  const reloadDetail = detail.reload;
  const reloadThread = thread.reload;
  const reloadNotes = notes.reload;
  const reloadDraft = draft.reload;
  useEffect(() => {
    if (tick === 0) return;
    void reloadDetail();
    void reloadThread();
    void reloadNotes();
    void reloadDraft();
  }, [tick, reloadDetail, reloadThread, reloadNotes, reloadDraft]);
  const [suggesting, setSuggesting] = useState(false);
  const [mode, setMode] = useState<'reply' | 'note'>('reply');
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const bottom = useRef<HTMLDivElement | null>(null);
  const messageCount = thread.data?.messages.length ?? 0;

  useEffect(() => {
    bottom.current?.scrollIntoView({ block: 'end' });
  }, [messageCount, id]);

  // Opening a thread marks it read.
  const unread = detail.data?.conversation.unread ?? 0;
  const onChangedRef = useRef(onChanged);
  useEffect(() => {
    onChangedRef.current = onChanged;
  });
  useEffect(() => {
    if (unread > 0) {
      void api.post(`/inbox/conversations/${id}/read`).then(() => onChangedRef.current());
    }
  }, [id, unread]);

  const c = detail.data?.conversation;
  const canReply = can('inbox:reply');
  const suggestions = useMemo(() => {
    if (!text.startsWith('/')) return [];
    const needle = text.slice(1).toLowerCase();
    return quick.filter((r) => r.shortcut.startsWith(needle)).slice(0, 5);
  }, [text, quick]);

  if (detail.loading && !detail.data) {
    return (
      <section className="inbox__thread">
        <div className="page-loading">
          <Spinner size="lg" label="Loading" />
        </div>
      </section>
    );
  }
  if (detail.error || !c) {
    return (
      <section className="inbox__thread">
        <Alert tone="critical">{detail.error ?? 'Conversation not found'}</Alert>
      </section>
    );
  }

  const patch = async (body: { status?: ConvStatus; assigneeUserId?: string | null }) => {
    try {
      await api.patch(`/inbox/conversations/${id}`, body);
      await onChanged();
    } catch (err) {
      toast.error(errorMessage(err));
    }
  };

  const submit = async () => {
    const value = text.trim();
    if (!value) return;
    setSending(true);
    try {
      if (mode === 'reply') {
        await api.post(`/inbox/conversations/${id}/messages`, {
          text: value,
          idempotencyKey: crypto.randomUUID(),
        });
      } else {
        await api.post(`/inbox/conversations/${id}/notes`, { body: value });
      }
      setText('');
      await onChanged();
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setSending(false);
    }
  };

  const suggest = async () => {
    setSuggesting(true);
    try {
      const r = await api.post<{ reply: string; escalate: string | null; sources: string[] }>(
        `/ai/conversations/${id}/suggestion`,
      );
      if (r.reply) {
        setMode('reply');
        setText(r.reply);
      } else {
        toast.error('The assistant had no suggestion for this message');
      }
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setSuggesting(false);
    }
  };

  const resumeAi = async () => {
    const ok = await confirm({
      title: 'Hand this conversation back to the assistant?',
      message: 'The AI assistant will answer the customer’s next message.',
      confirmLabel: 'Hand back',
    });
    if (!ok) return;
    try {
      await api.post(`/inbox/conversations/${id}/ai-resume`);
      await onChanged();
    } catch (err) {
      toast.error(errorMessage(err));
    }
  };

  const deleteNote = async (noteId: string) => {
    try {
      await api.delete(`/inbox/conversations/${id}/notes/${noteId}`);
      await onChanged();
    } catch (err) {
      toast.error(errorMessage(err));
    }
  };

  const canAssignOthers = can('inbox:assign');
  const composerBlocked = mode === 'reply' && !c.replyOpen;

  return (
    <>
      <section className="inbox__thread" aria-label="Conversation">
        <header className="inbox-head">
          <Avatar name={contactLabel(c)} />
          <div className="inbox-head__text">
            <h2 className="inbox-head__name">{contactLabel(c)}</h2>
            <span className="inbox-head__sub">
              <ChannelIcon channel={c.channel} />{' '}
              {c.channel === 'whatsapp'
                ? `+${c.contactId}`
                : c.contactHandle
                  ? `@${c.contactHandle}`
                  : c.contactId}
              {c.channelAccount ? ` · via ${c.channelAccount}` : ''}
            </span>
          </div>
          {canReply && (
            <div className="inbox-head__actions">
              {c.status !== 'resolved' ? (
                <Button size="sm" onClick={() => void patch({ status: 'resolved' })}>
                  Resolve
                </Button>
              ) : (
                <Button size="sm" onClick={() => void patch({ status: 'open' })}>
                  Reopen
                </Button>
              )}
            </div>
          )}
        </header>

        {c.aiState === 'escalated' && (
          <Alert tone="warning" title="This conversation needs a person">
            {c.escalationReason ?? 'The assistant handed it over.'}
          </Alert>
        )}

        <div className="inbox-messages" role="log" aria-live="polite">
          {thread.data?.hasMore && (
            <p className="inbox-messages__note">Showing the latest 100 messages.</p>
          )}
          {(thread.data?.messages ?? []).map((m) => (
            <MessageBubble key={m.id} m={m} />
          ))}
          {(notes.data ?? []).map((n) => (
            <div key={n.id} className="inbox-note">
              <StickyNote size={14} aria-hidden="true" />
              <div>
                <div className="inbox-note__meta">
                  Note by {n.author ?? 'someone'} · {formatDateTime(n.createdAt)}
                </div>
                <p>{n.body}</p>
              </div>
              {(n.authorId === myId || canAssignOthers) && (
                <button type="button" className="link-button" onClick={() => void deleteNote(n.id)}>
                  Delete
                </button>
              )}
            </div>
          ))}
          <div ref={bottom} />
        </div>

        {canReply ? (
          <div className={'inbox-composer' + (mode === 'note' ? ' inbox-composer--note' : '')}>
            <div className="inbox-mode" role="tablist" aria-label="Message type">
              <button
                type="button"
                role="tab"
                aria-selected={mode === 'reply'}
                className={'inbox-mode__tab' + (mode === 'reply' ? ' inbox-mode__tab--reply' : '')}
                onClick={() => setMode('reply')}
              >
                <MessageCircle size={15} aria-hidden="true" />
                <span>Reply to customer</span>
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={mode === 'note'}
                className={'inbox-mode__tab' + (mode === 'note' ? ' inbox-mode__tab--note' : '')}
                onClick={() => setMode('note')}
              >
                <StickyNote size={15} aria-hidden="true" />
                <span>Internal note</span>
              </button>
            </div>
            {mode === 'note' && (
              <p className="inbox-mode__hint">
                Only your team can see notes. Nothing is sent to the customer.
              </p>
            )}
            {mode === 'reply' && !composerBlocked && draft.data && text === '' && (
              <Alert tone="info" title="The assistant drafted a reply">
                <p className="inbox-draft">{draft.data.text}</p>
                <Button size="sm" onClick={() => setText(draft.data?.text ?? '')}>
                  Use this draft
                </Button>
              </Alert>
            )}
            {composerBlocked ? (
              <Alert tone="info">
                {c.channel === 'whatsapp' ? (
                  <>
                    The 24-hour WhatsApp window is closed. Send an approved template from{' '}
                    <Link to="/whatsapp">WhatsApp</Link> to start the conversation again.
                  </>
                ) : (
                  'Instagram only allows replies within 7 days of the customer’s last message.'
                )}
              </Alert>
            ) : (
              <>
                {suggestions.length > 0 && (
                  <ul className="inbox-quick">
                    {suggestions.map((s) => (
                      <li key={s.id}>
                        <button type="button" onClick={() => setText(s.body)}>
                          <strong>/{s.shortcut}</strong> <span>{s.title}</span>
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
                <TextAreaField
                  label={mode === 'reply' ? 'Message' : 'Note (only your team sees this)'}
                  rows={3}
                  value={text}
                  maxLength={mode === 'reply' ? 4096 : 4000}
                  placeholder={
                    mode === 'reply' ? 'Write a reply, or type / for quick replies' : 'Write a note'
                  }
                  onChange={(e) => setText(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) void submit();
                  }}
                />
                <div className="form-actions">
                  <span className="field__hint">Ctrl + Enter to send</span>
                  {mode === 'reply' && (
                    <Button
                      size="sm"
                      icon={<Bot size={14} />}
                      loading={suggesting}
                      onClick={() => void suggest()}
                    >
                      Suggest a reply
                    </Button>
                  )}
                  <Button
                    variant="primary"
                    loading={sending}
                    disabled={text.trim() === ''}
                    onClick={() => void submit()}
                  >
                    {mode === 'reply' ? 'Send' : 'Add note'}
                  </Button>
                </div>
              </>
            )}
          </div>
        ) : (
          <Alert tone="info">You have read-only access to the inbox.</Alert>
        )}
      </section>

      <aside className="inbox__side" aria-label="Details">
        <section className="inbox-panel">
          <h3 className="section-title">Handling</h3>
          <SelectField
            label="Status"
            value={c.status}
            disabled={!canReply}
            onChange={(e) => void patch({ status: e.target.value as ConvStatus })}
            options={(['open', 'pending', 'resolved'] as const).map((s) => ({
              value: s,
              label: STATUS_LABEL[s],
            }))}
          />
          <SelectField
            label="Assigned to"
            value={c.assignee?.id ?? ''}
            disabled={!canReply}
            onChange={(e) => void patch({ assigneeUserId: e.target.value || null })}
            options={[
              { value: '', label: 'Unassigned' },
              ...assignees
                .filter((a) => canAssignOthers || a.id === myId || a.id === c.assignee?.id)
                .map((a) => ({ value: a.id, label: a.name + (a.id === myId ? ' (you)' : '') })),
            ]}
          />
          <div className="inbox-panel__ai">
            <Bot size={16} aria-hidden="true" />
            <span>
              Assistant:{' '}
              <strong>
                {c.aiState === 'active'
                  ? 'answering'
                  : c.aiState === 'paused'
                    ? 'paused (a person is handling this)'
                    : 'stopped, needs a person'}
              </strong>
            </span>
          </div>
          {c.aiState !== 'active' && canAssignOthers && (
            <Button size="sm" icon={<Zap size={14} />} onClick={() => void resumeAi()}>
              Hand back to assistant
            </Button>
          )}
        </section>

        <section className="inbox-panel">
          <h3 className="section-title">Customer</h3>
          {detail.data?.customer ? (
            <dl className="dl">
              <dt>Name</dt>
              <dd>{detail.data.customer.name ?? '—'}</dd>
              <dt>Email</dt>
              <dd>{detail.data.customer.email ?? '—'}</dd>
              <dt>Orders</dt>
              <dd>
                {detail.data.customer.ordersCount} · {detail.data.customer.totalSpent} spent
              </dd>
              <dt>Last order</dt>
              <dd>
                {detail.data.customer.lastOrderAt
                  ? formatDateTime(detail.data.customer.lastOrderAt)
                  : '—'}
              </dd>
            </dl>
          ) : (
            <p className="field__hint">
              Not linked to a customer yet. Customers are matched by phone number when they have
              ordered from a connected store.
            </p>
          )}
        </section>

        <section className="inbox-panel">
          <h3 className="section-title">Activity</h3>
          {(detail.data?.events ?? []).length === 0 ? (
            <p className="field__hint">Nothing yet.</p>
          ) : (
            <ul className="inbox-events">
              {(detail.data?.events ?? []).map((e) => (
                <li key={e.id}>
                  <strong>{EVENT_LABEL[e.kind] ?? e.kind}</strong>
                  {e.actor ? ` by ${e.actor}` : ''}
                  <span> · {formatRelative(e.createdAt)}</span>
                </li>
              ))}
            </ul>
          )}
        </section>
      </aside>
    </>
  );
}

const STATUS_TEXT: Record<string, string> = {
  queued: 'Sending…',
  sending: 'Sending…',
  sent: 'Sent',
  delivered: 'Delivered',
  read: 'Read',
  failed: 'Failed',
};

function MessageBubble({ m }: { m: ThreadMessage }) {
  const out = m.direction === 'outbound';
  const label =
    m.body ??
    (m.templateName ? `Template: ${m.templateName}` : m.type !== 'text' ? `[${m.type}]` : '');
  return (
    <div className={'bubble' + (out ? ' bubble--out' : '')}>
      <div className="bubble__body">{label}</div>
      <div className="bubble__meta">
        {out && m.origin === 'ai' && <Badge tone="info">Assistant</Badge>}
        {out && m.campaign && <Badge>Campaign</Badge>}
        {out && m.sentBy && <span>{m.sentBy} · </span>}
        <span>{formatDateTime(m.sentAt)}</span>
        {out && (
          <span className={m.status === 'failed' ? 'bubble__failed' : undefined}>
            {' · '}
            {STATUS_TEXT[m.status] ?? m.status}
            {m.status === 'failed' && m.errorTitle ? ` (${m.errorTitle.replace(/_/g, ' ')})` : ''}
          </span>
        )}
      </div>
    </div>
  );
}
